import { FieldComponent } from '@components/forms/FieldComponent';
import { useFormStore } from '@components/forms/Form';

import { TFormField } from '@models/forms';
import { Box, Grid, Theme, Typography } from '@mui/material';
import { adjustFontSize } from '@theme/styleOverrides/typography';
import React from 'react';

type FormSectionProps = {
  /** Optional title for the form section */
  title?: string;
  subTitle?: string;
  fields: { [fieldName: string]: TFormField };
  isReadOnly?: boolean;
  isDisabled?: boolean;
  hideVisibleFieldsInDom?: boolean;
  styleConfig?: {
    spacing?: number;
    marginTop?: string | number;
    marginBottom?: string | number;
    justifyContent?: string;
    alignItems?: string;
  };
};

export const FormSection = ({
  styleConfig,
  title,
  subTitle,
  fields,
  hideVisibleFieldsInDom,
  isReadOnly = false,
  isDisabled = false,
}: FormSectionProps) => {
  const { watch } = useFormStore((state) => state);

  const spacing = styleConfig?.spacing ?? 1;
  return (
    <Grid
      container
      columnSpacing={spacing}
      sx={{
        width: '100%',
        justifyContent: styleConfig?.justifyContent ?? '',
        marginTop: styleConfig?.marginTop ?? '10px',
        marginBottom: styleConfig?.marginBottom ?? '10px',
        alignItems: styleConfig?.alignItems ?? 'initial',
      }}
    >
      {(title || subTitle) && (
        <Grid size={{ xs: 12 }}>
          {title && (
            <Typography variant='h4' mb={1}>
              {title}
            </Typography>
          )}
          {subTitle && (
            <Typography
              variant='body'
              display='block'
              sx={{
                mb: 2,
                color: 'text.subdued',
              }}
            >
              {subTitle}
            </Typography>
          )}
        </Grid>
      )}
      {Object.entries(fields).map(([name, field]) => {
        const watchedField = field.watchedField ? watch(field.watchedField) : undefined;
        const watchedFields = field.watchedFields
          ? field.watchedFields.reduce(
              (acc, curr) => {
                acc[curr] = watch(curr);
                return acc;
              },
              {} as { [key: string]: any },
            )
          : undefined;

        const fieldIsVisible =
          field.isVisible === undefined ||
          (typeof field.isVisible === 'function' && field.isVisible({ data: { watchedField, ...watchedFields } })) ||
          (typeof field.isVisible === 'boolean' && field.isVisible);

        if (!fieldIsVisible) {
          if (!hideVisibleFieldsInDom) return null;

          // this is necessary so isVisible:false but isActive:true fields remain in the form context
          return <Grid key={name} sx={{ position: 'absolute', display: 'none' }} />;
        }

        return (
          <React.Fragment key={name}>
            {field.fieldConfig?.requireNewLine && <Box key={`${name}-break`} sx={{ width: '100%', padding: 0 }} />}
            <Grid
              key={name}
              size={{ xs: field.gridWidth.xs, sm: field.gridWidth.sm, md: field.gridWidth.md, lg: field.gridWidth.lg }}
              sx={{
                display: 'block',
                '& .MuiInputLabel-root': {
                  fontSize: ({ palette }: Theme) => `${adjustFontSize(14, palette.fontSize)}px !important`,
                },
                paddingBottom: spacing,
                ...field.sx,
              }}
            >
              <FieldComponent field={field} forceDisabled={isDisabled || isReadOnly} />
            </Grid>
          </React.Fragment>
        );
      })}
    </Grid>
  );
};
